import type { CostAndCharge } from 'models/streaming/costsAndCharges';
import { type IntlShape, useIntl } from 'react-intl';

interface IFormattedCostAndCharge {
  id: string;
  name: string;
  category: string;
  value: string;
}

const costsAndChargesNames = {
  totalCostsPercentage: 'TotalCosts_Upfront',
  totalCostsCurrency: 'TotalCosts_UpfrontCur',
  nominal: 'Nominal',
};

export function useCostAndChargesDescription(costsAndCharges: CostAndCharge[]) {
  const { formatMessage, formatNumber } = useIntl();
  const totalCostsPercentage = costsAndCharges.find(e => e.name === costsAndChargesNames.totalCostsPercentage);
  const totalCostsCurrency = costsAndCharges.find(e => e.name === costsAndChargesNames.totalCostsCurrency);
  const nominal = costsAndCharges.find(e => e.name === costsAndChargesNames.nominal);
  return formatMessage(
    { id: 'costAndCharge.description' },
    {
      totalCostsDisclose: totalCostsPercentage ? formatCostAndChargeValue(totalCostsPercentage, formatNumber) : '-',
      totalCostsDiscloseCur: totalCostsCurrency ? formatCostAndChargeValue(totalCostsCurrency, formatNumber) : '-',
      nominal: nominal ? formatCostAndChargeValue(nominal, formatNumber) : '-',
    },
  );
}

function formatCostAndChargeName(
  name: string,
  currency: string | undefined,
  formatMessage: IntlShape['formatMessage'],
): string {
  return formatMessage({ id: `costAndCharge.${name}` as any, defaultMessage: formatToSentence(name) }, { currency });
}

function formatToSentence(value: string): string {
  return value
    .replace(/_/g, ' ')
    .replace(/([^ ])(?=[A-Z])/g, '$1 ')
    .trim();
}

export function useFormatAndSortCostsAndCharges(costsAndCharges: CostAndCharge[]): IFormattedCostAndCharge[] {
  const { formatMessage, formatNumber } = useIntl();
  return costsAndCharges
    .map<IFormattedCostAndCharge>(costAndCharge => ({
      id: costAndCharge.name,
      name: formatCostAndChargeName(costAndCharge.name, costAndCharge.unit, formatMessage),
      value: formatCostAndChargeValue(costAndCharge, formatNumber),
      category: formatToSentence(costAndCharge.category),
    }))
    .sort(costAndChargeComparer);
}

function formatCostAndChargeValue(costAndCharge: CostAndCharge, formatNumber: IntlShape['formatNumber']): string {
  switch (costAndCharge.valueFormat) {
    case 'Percentage':
      return formatNumber(costAndCharge.value, { maximumFractionDigits: 4, minimumFractionDigits: 4 }).concat(' %');
    case 'Currency':
      return formatNumber(costAndCharge.value, { maximumFractionDigits: 2, minimumFractionDigits: 2 })
        .concat(' ')
        .concat(costAndCharge.unit!);
    default:
      return formatNumber(costAndCharge.value, { maximumFractionDigits: 10 });
  }
}

function costAndChargeComparer(c1: IFormattedCostAndCharge, c2: IFormattedCostAndCharge) {
  return c1.category.localeCompare(c2.category) || c1.name.localeCompare(c2.name);
}
